import { Card, CardBody, CardHeader, Divider } from "@heroui/react";
import { DateValue } from "@internationalized/date";
import i18nInstance from "@/i18n";

type Change = {
  original: any;
  updated: any;
};

const formatValue = (value: string | DateValue | undefined) => {
  if (value === undefined || value === null) return "";

  if (typeof value === "object" && "toLocaleString" in value) {
    return value.toLocaleString();
  }

  return String(value);
};

/**
 * Component that displays differences between original and updated values
 *
 * @param changes - Record of field names and their original/updated values
 * @param exclude_key - Array of keys to exclude from display
 * @param translations - Optional record for translating field names
 * @returns JSX element displaying the differences
 */
function Diff(
  changes: Record<string, Change>,
  exclude_key: ReadonlyArray<string> = [],
  translations?: Record<string, string>,
  tableName?: string,
) {
  const resolveLabel = (key: string) => {
    if (tableName) {
      const fieldsKey = `${tableName}.fields.${key}`;
      const fieldsLabel = i18nInstance.t(fieldsKey, {
        ns: "tables",
        defaultValue: fieldsKey,
      });
      if (fieldsLabel !== fieldsKey) return fieldsLabel;

      const columnsKey = `${tableName}.columns.${key}`;
      const columnsLabel = i18nInstance.t(columnsKey, {
        ns: "tables",
        defaultValue: columnsKey,
      });
      if (columnsLabel !== columnsKey) return columnsLabel;
    }

    return translations?.[key] || key;
  };

  return Object.entries(changes)
    .filter(([key]) => !exclude_key.includes(key))
    .map(([key, { original, updated }]) => (
      <Card key={key} className="mb-4 min-h-min">
        <CardHeader className="text-lg font-semibold text-gray-800">
          {resolveLabel(key)}
        </CardHeader>
        <Divider />
        <CardBody className="text-lg font-semibold bg-slate-100">
          <div className="text-red-600">
            <span className="font-mono text-sm">
              - {formatValue(original) ?? "N/A"}
            </span>
          </div>
          <div className="text-green-600">
            <span className="font-mono text-sm">
              + {formatValue(updated) ?? "N/A"}
            </span>
          </div>
        </CardBody>
      </Card>
    ));
}

export { Diff, type Change };
